import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  Settings,
  MsSettingsProfilePage,
  MsSettingsSecurityPage,
  MsSettingsNotificationsPage,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export enum MS_SETTINGS_PATH_SEGMENTS {
  ROOT = "settings",
  PROFILE = "profile-settings",
  SECURITY = "security-settings",
  NOTIFICATIONS = "notification-settings",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const myspaceSettingsRoutes: RouteObject = {
  path: MS_SETTINGS_PATH_SEGMENTS.ROOT,
  // `settingsSpace`: the app Layout swaps its main menu for the settings
  // sidebar on every route under this one (see useSettingsRouteNav).
  handle: { breadcrumb: "Settings", settingsSpace: "myspace" },
  HydrateFallback: Spinner,
  children: [
    // ── Settings page frame (SettingsLayout), inside the app Layout, which
    // puts the settings sidebar in its main menu's slot. Each section is a
    // path segment; its sub-sections stay a query param (?vTab=...) and are
    // listed in the settings sidebar (config/settings-nav.config.ts). ───────
    {
      Component: Settings,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(MS_SETTINGS_PATHS.TAB_PROFILE),
        },
        {
          path: MS_SETTINGS_PATH_SEGMENTS.PROFILE,
          handle: { breadcrumb: "Profile Settings" },
          Component: MsSettingsProfilePage,
        },
        {
          path: MS_SETTINGS_PATH_SEGMENTS.SECURITY,
          handle: { breadcrumb: "Security Settings" },
          Component: MsSettingsSecurityPage,
        },
        {
          path: MS_SETTINGS_PATH_SEGMENTS.NOTIFICATIONS,
          handle: { breadcrumb: "Notification Settings" },
          Component: MsSettingsNotificationsPage,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
const _MS_SETTINGS_ROOT = addSlashes(MS_SETTINGS_PATH_SEGMENTS.ROOT);
// hTab is now a real path segment; vTab stays a query param scoped to that page.
const _msTab  = (tab: string)             => `${_MS_SETTINGS_ROOT}/${tab}`;
const _msVTab = (tab: string, vTab: string) => `${_MS_SETTINGS_ROOT}/${tab}?vTab=${vTab}`;

export const MS_SETTINGS_PATHS = Object.freeze({
  ROOT: _MS_SETTINGS_ROOT,

  // ── hTab tab routes ───────────────────────────────────────────────────────
  TAB_PROFILE:       _msTab("profile-settings"),
  TAB_SECURITY:      _msTab("security-settings"),
  TAB_NOTIFICATIONS: _msTab("notification-settings"),

  // ── vTab deep-links — each one is an item in the settings sidebar
  // (config/settings-nav.config.ts) ─────────────────────────────────────────
  VTAB_PROFILE_PERSONAL:        _msVTab("profile-settings", "personal-information"),
  VTAB_PROFILE_EMPLOYMENT:      _msVTab("profile-settings", "employment-information"),
  VTAB_PROFILE_COMPENSATION:    _msVTab("profile-settings", "compensation-information"),
  VTAB_PROFILE_BANKING:         _msVTab("profile-settings", "banking-details"),

  VTAB_SECURITY_PASSWORD:       _msVTab("security-settings", "password"),
  VTAB_SECURITY_MFA:            _msVTab("security-settings", "multi-factor-auth"),
  VTAB_SECURITY_ACTIVE_SESSIONS:_msVTab("security-settings", "active-sessions"),
  VTAB_SECURITY_LOGIN_HISTORY:  _msVTab("security-settings", "login-history"),

  VTAB_NOTIFICATIONS_METHOD:    _msVTab("notification-settings", "notification-method"),
});
